import {Hotkey} from '@wandb/components';
import {
  Content as DropdownContent,
  Item as DropdownItem,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
  Trigger as DropdownTrigger,
} from '@wandb/weave/components/DropdownMenu';
import * as Switch from '@wandb/weave/components/Switch';
import React, {memo, useCallback, useState} from 'react';

import {INPUT_ACTION, useRegisterAction} from '../inputSystem';
import {useIsInLoadedReportContext} from '../pages/ReportPage/LoadedReportContext';
import {
  trackRunVisibilityMenu,
  type VisibilityMenuAction,
} from '../services/analytics/runSelectAllVisibilityMenuEvents';
import {DD_PRIVACY_ALLOW} from '../util/datadog/privacy';
import {MultiStateCheckbox} from './MultiStateCheckbox';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface RunSelectAllProps {
  allCheckedState: 'unchecked' | 'checked' | 'partial' | 'unknown';
  onlyShowSelected?: boolean;
  setOnlyShowSelected?(newSetting: boolean): void;
  selectAllVisible(): void; // copies visualized runs to temp selections
  onSelectAll(): void;
  onSelectNone(): void;
  onSelectVisibleRuns(): void;
}

const RunSelectAllComp: React.FC<RunSelectAllProps> = ({
  allCheckedState,
  onSelectAll,
  onSelectNone,
  onSelectVisibleRuns,
  onlyShowSelected,
  setOnlyShowSelected,
  selectAllVisible,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const isInReport = useIsInLoadedReportContext();
  const trackVisibilityMenuWithLocation = useCallback(
    (action: VisibilityMenuAction, method: 'hotkey' | 'dropdown') => {
      trackRunVisibilityMenu({action, method, location: analyticsLocation});
    },
    [analyticsLocation]
  );

  useRegisterAction(INPUT_ACTION.TOGGLE_VISUALIZED_RUNS, () => {
    if (isInReport || !setOnlyShowSelected) {
      return;
    }
    trackVisibilityMenuWithLocation('Only show visualized toggled', 'hotkey');
    setOnlyShowSelected(!onlyShowSelected);
  });

  return (
    <DropdownRoot
      open={isMenuOpen}
      onOpenChange={(open: boolean) => {
        if (open) {
          trackVisibilityMenuWithLocation('Visibility menu opened', 'dropdown');
        }
        setIsMenuOpen(open);
      }}>
      <DropdownTrigger>
        <button
          type="button"
          style={{all: 'unset', cursor: 'pointer'}}
          data-dd-action-name="Run visibility options"
          data-dd-privacy={DD_PRIVACY_ALLOW}>
          <MultiStateCheckbox
            data-test="all-runs-visibility-checkbox"
            aria-label="Visibility options"
            checked={allCheckedState}
            theme="visibility"
          />
        </button>
      </DropdownTrigger>
      <DropdownPortal>
        <DropdownContent
          align="start"
          sideOffset={8}
          alignOffset={0}
          // above WBTable sticky chrome (max z:105); see WBTable.less.
          className="z-[200]">
          <DropdownItem
            data-dd-action-name="Make all runs visible"
            data-dd-privacy={DD_PRIVACY_ALLOW}
            onSelect={() => {
              onSelectAll();
              trackVisibilityMenuWithLocation('Make all visible', 'dropdown');
            }}>
            Make all visible
          </DropdownItem>
          <DropdownItem
            data-dd-action-name="Make all runs hidden"
            data-dd-privacy={DD_PRIVACY_ALLOW}
            onSelect={() => {
              onSelectNone();
              trackVisibilityMenuWithLocation('Make all hidden', 'dropdown');
            }}>
            Make all hidden
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem
            data-dd-action-name="List only visible runs toggle"
            data-dd-privacy={DD_PRIVACY_ALLOW}
            onSelect={e => {
              e.preventDefault();
              if (setOnlyShowSelected) {
                setOnlyShowSelected(!onlyShowSelected);
              }
              trackVisibilityMenuWithLocation(
                'Only show visualized toggled',
                'dropdown'
              );
            }}>
            <span className="pr-10">List only visible runs</span>
            {!isInReport && (
              <Hotkey keys={['Alt', 'KeyV']} name="List only visible runs" />
            )}
            <Switch.Root checked={onlyShowSelected}>
              <Switch.Thumb checked={onlyShowSelected ? true : false} />
            </Switch.Root>
          </DropdownItem>
          <DropdownItem
            data-dd-action-name="Select visible runs"
            data-dd-privacy={DD_PRIVACY_ALLOW}
            onSelect={() => {
              selectAllVisible();
              trackVisibilityMenuWithLocation(
                'Select visible runs',
                'dropdown'
              );
              onSelectVisibleRuns();
            }}>
            Select visible runs
          </DropdownItem>
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
};

export const RunSelectAll = memo(RunSelectAllComp);
